import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import RfqQuoteDetailProposedDetailsGridItem, {
    RfqQuoteDetailProposedDetailGridItemStyles,
} from "@MasterBluePrint/Widgets/RfqQuoteDetail/RfqQuoteDetailProposedDetailsGridItem";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

type Props = ReturnType<typeof mapStateToProps>;

export interface RfqQuoteDetailProposedDetailGridStyles {
    mainWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    countLabelText?: TypographyPresentationProps;
    countValueText?: TypographyPresentationProps;
    itemStyles?: RfqQuoteDetailProposedDetailGridItemStyles;
    footerWrapper?: InjectableCss;
    totalLabelText?: TypographyPresentationProps;
    totalValueText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    totalWithoutVatText?: TypographyPresentationProps;


    gridContainer?: GridContainerProps;
    quoteLineGridHeadWrapper?: GridItemProps;
    quoteLineGridHead?: InjectableCss;
}

export const rfqQuoteDetailProposedDetailGridStyles: RfqQuoteDetailProposedDetailGridStyles = {
    gridContainer: {
        gap: 10,
    },
    quoteLineGridHeadWrapper: {
        width: 12,
        css: css`
            padding: 0.625rem 0.9375rem 0.625rem 0;
            margin: 0 0.625rem 0 0.625rem !important;
            border-top: 1px solid ${themeColors.colorBorderBrands};
            border-bottom: 1px solid ${themeColors.colorBorderBrands};

            .total-items {
                min-width: 18.75rem;
            }
        `,
    },
    quoteLineGridHead: {
        css: css`
            display: block;
            width: 100%;

            h3 {
                color: ${themeColors.colorActiveMenuLink};
                font-family: 'Lato',sans-serif;
                font-size: ${themeFontSizes.size16};
                font-weight: bold;
                letter-spacing: 0.5px;
                line-height: 24px;
            }

            .total-items {
                h3 {
                    color: ${themeColors.colorPrimary};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size16};
                    font-style: italic;
                    font-weight: ${themeFontWeights.fontWeightMedium};
                    letter-spacing: 0;
                }
            }

            > div {
                display: inline-block;
                text-align: left;
            }

            .sub-total {
                display: inline-block;
                text-align: right;
                float: right;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            h3, .price, .qty {
                                display: none;
                            }
                            .total-items {
                                h3 {
                                    display: block;
                                }
                            }
                            > div {
                                display: block;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            }
        `,
    },

    headerWrapper: {
        css: css`
            display: flex;
            padding-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    countLabelText: {
        weight: 600,
        css: css`
            margin-left: 5px;
        `,
    },
    countValueText: {
        weight: 600,
    },
    footerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: flex-end;
            margin-top: 20px;
        `,
    },
    totalLabelText: {
        size: 20,
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    totalValueText: {
        size: 20,
    },
    vatLabelText: {
        size: 12,
    },
    totalWithoutVatText: {
        size: 20,
        css: css`
            margin-top: 5px;
        `,
    },
};

const styles = rfqQuoteDetailProposedDetailGridStyles;

const RfqQuoteDetailProposedDetailsGrid = ({ quoteState, enableVat, vatPriceDisplay }: Props) => {
    const quote = quoteState.value;
    if (!quote || !quote.quoteLineCollection) {
        return null;
    }

    const quotesCount = quote?.quoteLineCollection.length;
    const quotesLabel = quotesCount > 1 ? "Items in this Quote" : "Item in this Quote";

    function setWidthFromOffset() {
        setTimeout(() => {
            const elementPriceContainer = document.getElementById("priceContainer");
            const elementTotalItems = document.getElementById("totalItems");
            const elementPriceItem = document.getElementById("priceItem");
            const elementQtyItem = document.getElementById("quote-qty");
            const elementAvailItem = document.getElementById("quote-availability");
            const elementItemPriceHeading = document.getElementById("itemPrice");
            const elementItemQtyHeading = document.getElementById("itemQty");
            const elementItemAvailHeading = document.getElementById("itemAvail");
            const elementOrderLineCard = document.querySelectorAll('.quote-line-wrapper')[0];

            if (elementPriceContainer && elementTotalItems && elementPriceItem && elementItemPriceHeading && elementOrderLineCard && elementQtyItem && elementItemQtyHeading && elementAvailItem && elementItemAvailHeading ) {
                const leftOffsetNew = elementPriceContainer.getBoundingClientRect().left - elementOrderLineCard.getBoundingClientRect().left;
                const priceEleWidth = elementPriceItem.offsetWidth;
                const qtyEleWidth = elementQtyItem.offsetWidth;
                 const availEleWidth = elementAvailItem.offsetWidth;
                elementTotalItems.style.width = `${leftOffsetNew + 6}px`;
                elementItemPriceHeading.style.width = `${priceEleWidth}px`;
                elementItemQtyHeading.style.width = `${qtyEleWidth}px`;
                 elementItemAvailHeading.style.width = `${availEleWidth}px`;
            }
        }, 500);
    }

    if (document.body.clientWidth > 767) {
        setWidthFromOffset();
    }

    window.addEventListener("resize", setWidthFromOffset);
    
    return (
        <StyledWrapper {...styles.mainWrapper} className="main-quote-wrapper">
            <GridContainer  {...styles.gridContainer}>
                <GridItem  {...styles.quoteLineGridHeadWrapper} data-test-selector="quote-grid-header">
                    <StyledWrapper {...styles.quoteLineGridHead}>
                        <Typography id="totalItems" as="div" className="total-items"><Typography as="h3">{quotesCount} {translate(quotesLabel)}</Typography></Typography>
                        <Typography id="itemPrice" as="div" className="price"><Typography as="h3">{translate("Price")}</Typography></Typography>
                        <Typography id="itemQty" as="div" className="qty"><Typography as="h3">{translate("QTY")}</Typography></Typography>
                        <Typography id="itemAvail" as="div" className="avaiability"><Typography as="h3">{translate("Availability")}</Typography></Typography>
                        <Typography as="div" className="sub-total"><Typography as="h3">{translate("SubTotal")}</Typography></Typography>
                    </StyledWrapper>
                </GridItem>            
            
                {quote.quoteLineCollection.map(quoteLine => (
                    <RfqQuoteDetailProposedDetailsGridItem
                        key={`${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
                        quote={quote}
                        quoteLine={quoteLine}
                        extendedStyles={styles.itemStyles}
                    />
                ))}
            </GridContainer>      
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(RfqQuoteDetailProposedDetailsGrid);
